import { MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import { z } from "zod";

export type InlineTreatmentRecordFieldErrorKey =
  | "name"
  | "status"
  | "requestDate"
  | "receivedDate"
  | "rejectedDate";

export type InlineTreatmentRecordFieldErrors = Partial<
  Record<InlineTreatmentRecordFieldErrorKey, string>
>;

export type InlineTreatmentRecordFormValues = Partial<{
  name: string | null;
  status: string;
  requestDate: string | null;
  receivedDate: string | null;
  rejectedDate: string | null;
  rejectedReason: string | null;
}>;

const INLINE_TREATMENT_RECORD_FIELD_ERROR_KEYS =
  new Set<InlineTreatmentRecordFieldErrorKey>([
    "name",
    "status",
    "requestDate",
    "receivedDate",
    "rejectedDate",
  ]);

const isInlineTreatmentRecordFieldErrorKey = (
  field: string,
): field is InlineTreatmentRecordFieldErrorKey =>
  INLINE_TREATMENT_RECORD_FIELD_ERROR_KEYS.has(
    field as InlineTreatmentRecordFieldErrorKey,
  );

const inlineTreatmentRecordValidationSchema = z
  .object({
    name: z.string().trim().max(50, {
      message: "Record name must be less than 50 characters",
    }),
    status: z
      .enum(MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS)
      .or(z.literal(""))
      .refine((value) => value, { message: "Status is required" }),
    requestDate: z.string().nullable().optional(),
    receivedDate: z.string().nullable().optional(),
    rejectedDate: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (["Requested", "Received"].includes(data.status)) {
      if (!data.requestDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Request date is required",
          path: ["requestDate"],
        });
      }
    }
    if (data.status === "Received") {
      if (!data.receivedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Received date is required",
          path: ["receivedDate"],
        });
      }
    }
    if (data.status === "Rejected") {
      if (!data.rejectedDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Rejected date is required",
          path: ["rejectedDate"],
        });
      }
    }
  });

export const normalizeInlineTreatmentRecordFormValues = (
  values: InlineTreatmentRecordFormValues,
) => ({
  name: values.name ?? "",
  status: values.status ?? "",
  requestDate: values.requestDate || null,
  receivedDate: values.receivedDate || null,
  rejectedDate: values.rejectedDate || null,
});

export const validateInlineTreatmentRecordForm = (
  values: InlineTreatmentRecordFormValues,
): InlineTreatmentRecordFieldErrors => {
  const result = inlineTreatmentRecordValidationSchema.safeParse(
    normalizeInlineTreatmentRecordFormValues(values),
  );

  if (result.success) {
    return {};
  }

  const errors: InlineTreatmentRecordFieldErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (
      typeof field === "string" &&
      isInlineTreatmentRecordFieldErrorKey(field) &&
      !errors[field]
    ) {
      errors[field] = issue.message;
    }
  }

  if (Object.keys(errors).length === 0 && result.error.issues[0]) {
    errors.name = result.error.issues[0].message;
  }

  return errors;
};
